/** Header "Nowy koszyk" button — opens a token picker and creates a basket for the chosen token. */
import { Button } from "@client/components/ui/Button";
import { api } from "@client/lib/api";
import { invalidateAfterBasketChange, tokensQuery } from "@client/lib/queries";
import { useToast } from "@client/lib/toast";
import { price } from "@shared/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

type NewBasketButtonProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onFocusSearch: () => void;
};

export function NewBasketButton({ open, onOpenChange, onFocusSearch }: NewBasketButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tokensQ = useQuery(tokensQuery());
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onOpenChange(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, onOpenChange]);

  const createMutation = useMutation({
    mutationFn: (tokenId: string) => api.baskets.create(tokenId),
    onSuccess: async (basket) => {
      await invalidateAfterBasketChange(qc);
      onOpenChange(false);
      await navigate({ to: "/koszyki/$basketId", params: { basketId: basket.id } });
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Nie udało się utworzyć koszyka."),
  });

  const tokens = tokensQ.data ?? [];

  return (
    <div ref={containerRef} className="relative">
      <Button onClick={() => onOpenChange(!open)} aria-label="Nowy koszyk" className="px-2.5 sm:px-3.5">
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span className="hidden sm:inline">Nowy koszyk</span>
        <span className="mono hidden rounded border border-white/35 px-1 text-[11px] opacity-70 md:inline">
          n
        </span>
      </Button>
      {open && (
        <div className="absolute right-0 top-[42px] z-30 w-[min(18rem,calc(100vw-1.5rem))] overflow-hidden rounded-md border border-line-2 bg-surface shadow-md">
          {tokens.length === 0 ? (
            <div className="flex flex-col gap-2 p-3 text-sm text-fg-3">
              <span>Najpierw dodaj token do watchlisty.</span>
              <button
                type="button"
                className="self-start text-xs text-link hover:underline"
                onClick={() => {
                  onOpenChange(false);
                  onFocusSearch();
                }}
              >
                Przejdź do wyszukiwania
              </button>
            </div>
          ) : (
            tokens.map((token) => (
              <button
                key={token.id}
                type="button"
                disabled={createMutation.isPending}
                onClick={() => createMutation.mutate(token.id)}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-fg-1 hover:bg-line disabled:opacity-60"
              >
                <span className="w-14 shrink-0 font-semibold">{token.symbol}</span>
                <span className="min-w-0 flex-1 truncate text-fg-3">{token.name}</span>
                <span className="mono shrink-0 whitespace-nowrap">{price(token.priceUsd)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
